import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from 'reactstrap';

import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { useSelector } from 'react-redux';
import useVendorBranch from '../../utils/useVendorBranch';

const AddEditNotification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.authState);
  const { isSuperAdmin, isAdmin, hasMultiBranch } = useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});
  const [userProfileList, setUserProfileList] = useState([]);

  const [notificationData, setNotificationData] = useState({
    notification_id: 0,
    vendor_id: 0,
    branch_id: 0,

    title: '',
    message: '',
    notification_type: 'SYSTEM',

    // ✅ dropdown value (id as string): '' | '0' | '5'
    send_to_profile: '',

    schedule_for: '', // yyyy-mm-dd
    scheduled_time: '', // HH:mm

    sent_yn: false,
  });

  // ---------------- helpers ----------------
  const toDateInputValue = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && val.length >= 10)
      return val.substring(0, 10);
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // ✅ Handles: "2026-02-28T14:30:00" OR "14:30:00" OR "14:30"
  const toTimeInputValue = (val) => {
    if (!val) return '';

    if (typeof val === 'string') {
      if (val.includes('T')) {
        const timePart = val.split('T')[1]; // "14:30:00"
        if (timePart && timePart.includes(':')) return timePart.substring(0, 5);
        return '';
      }
      if (val.includes(':')) return val.substring(0, 5);
      return '';
    }

    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  // ✅ backend scheduled_time is DateTime? => must send ISO datetime
  const toIsoDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const d = dateStr.substring(0, 10); // yyyy-mm-dd
    const t = timeStr.length === 5 ? `${timeStr}:00` : timeStr; // HH:mm -> HH:mm:00
    return `${d}T${t}`; // yyyy-mm-ddTHH:mm:ss
  };

  // map dropdown id -> backend string
  const resolveSendToProfileString = (idStr) => {
    if (idStr === '' || idStr === null || idStr === undefined) return null;
    const id = Number(idStr);
    if (Number.isNaN(id)) return null;
    if (id === 0) return 'ALL';
    const p = userProfileList.find((x) => Number(x.id) === id);
    return (p?.user_profile || '').trim().toUpperCase() || null;
  };

  // ---------------- change handler ----------------
  const onChangeHandle = (e) => {
    const { name, value } = e.target;
    setNotificationData((prev) => ({ ...prev, [name]: value }));
  };

  // ---------------- load profiles ----------------
  const getUserProfile = async () => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
      };

      const response = await apiService.get(
        `api/admin/User_Detail/GetUserProfile`,
        config,
      );

      if (response?.status === true) {
        const list =
          response.user_Profile_Masters || response.User_Profile_Masters || [];
        setUserProfileList(list);
      } else {
        toast.error(
          response?.message || response?.Message || 'Unable to load profiles',
        );
      }
    } catch (e) {
      console.error(e);
      toast.error('SomeThing went wrong!!');
    }
  };

  useEffect(() => {
    getUserProfile();
  }, []);

  // ---------------- edit fill (FIXED) ----------------
  useEffect(() => {
    if (!location.state) return;

    const st = location.state || {};

    // ✅ resolve dropdown id from backend send_to_profile string
    const profileId =
      st.send_to_profile_id !== undefined && st.send_to_profile_id !== null
        ? String(st.send_to_profile_id)
        : (st.send_to_profile || '').trim().toUpperCase() === 'ALL'
          ? '0'
          : (() => {
              const match = userProfileList.find(
                (x) =>
                  (x.user_profile || '').trim().toUpperCase() ===
                  (st.send_to_profile || '').trim().toUpperCase(),
              );
              return match ? String(match.id) : '';
            })();

    setNotificationData((prev) => ({
      ...prev,
      ...st,
      notification_id: st.notification_id || st.id || 0,

      schedule_for: toDateInputValue(st.schedule_for),
      scheduled_time: toTimeInputValue(st.scheduled_time),

      // ✅ dropdown needs ID string
      send_to_profile: profileId,
    }));
  }, [location.state, userProfileList]);

  // ---------------- save ----------------
  const SaveNotification = async () => {
    if (
      isSuperAdmin &&
      (!notificationData.vendor_id || notificationData.vendor_id <= 0)
    ) {
      toast.error('Please select Vendor');
      return;
    }

    if (
      (isSuperAdmin || isAdmin || hasMultiBranch) &&
      (!notificationData.branch_id || notificationData.branch_id <= 0)
    ) {
      toast.error('Please select Branch');
      return;
    }

    if (!notificationData.title?.trim()) {
      toast.error('Please enter Title');
      return;
    }

    if (!notificationData.message?.trim()) {
      toast.error('Please enter Message');
      return;
    }

    if (!notificationData.notification_type) {
      toast.error('Please select Notification Type');
      return;
    }

    if (
      notificationData.send_to_profile === '' ||
      notificationData.send_to_profile === null
    ) {
      toast.error('Please select Send To Profile');
      return;
    }

    // schedule validation
    const isScheduled =
      !!notificationData.schedule_for || !!notificationData.scheduled_time;
    if (
      isScheduled &&
      (!notificationData.schedule_for || !notificationData.scheduled_time)
    ) {
      toast.error('Please select Schedule Date and Scheduled Time');
      return;
    }

    // dropdown id -> backend string
    const send_to_profile = resolveSendToProfileString(
      notificationData.send_to_profile,
    );
    if (!send_to_profile) {
      toast.error('Invalid Send To Profile');
      return;
    }

    try {
      let res;

      const payload = {
        notification_id: Number(notificationData.notification_id || 0),
        vendor_id: notificationData.vendor_id
          ? Number(notificationData.vendor_id)
          : null,
        branch_id: notificationData.branch_id
          ? Number(notificationData.branch_id)
          : null,

        title: notificationData.title.trim(),
        message: notificationData.message.trim(),
        notification_type: (notificationData.notification_type || '')
          .trim()
          .toUpperCase(),

        // ✅ backend expects this string field
        send_to_profile: send_to_profile,

        schedule_for: notificationData.schedule_for
          ? notificationData.schedule_for
          : null,

        // ✅ backend scheduled_time is DateTime? => must send ISO datetime
        scheduled_time:
          notificationData.schedule_for && notificationData.scheduled_time
            ? toIsoDateTime(
                notificationData.schedule_for,
                notificationData.scheduled_time,
              )
            : null,
      };

      // ✅ IMPORTANT:
      // If your API expects wrapper ("req field is required"), use this:
      // const body = { req: payload };
      // Otherwise direct payload:
      const body = payload;

      if (payload.notification_id > 0) {
        res = await apiService.put(
          `api/Notification/update?id=${payload.notification_id}`,
          body,
        );
      } else {
        res = await apiService.post(
          `api/Notification/add`,
          body,
        );
      }

      if (res?.status === true) {
        toast.success(res.message || res.Message || 'Saved successfully');
        navigate('/list-notification');
      } else {
        toast.error(res?.message || res?.Message || 'Failed');
      }
    } catch (e) {
      console.error(e);
      toast.error('Something went wrong');
    }
  };

  return (
    <>
      {/* ✅ Weekend style: selector OUTSIDE card */}
      <VendorBranchSelector
        user={user}
        selectionData={notificationData}
        setSelectionData={setNotificationData}
        accessButton={true}
        errorMessage={errorMessage}
      />

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='border-bottom p-3 d-flex justify-content-between align-items-center'>
          <span>
            {notificationData.notification_id > 0
              ? 'Edit Notification'
              : 'Add Notification'}
          </span>

          <Button color='warning' onClick={() => navigate(-1)}>
            <i className='bi bi-arrow-left' />
          </Button>
        </CardTitle>

        <CardBody>
          <Row>
            <FormGroup className='col-sm-6'>
              <Label>Title</Label>
              <Input
                type='text'
                name='title'
                value={notificationData.title}
                onChange={onChangeHandle}
                placeholder='Enter title'
              />
            </FormGroup>

            <FormGroup className='col-sm-6'>
              <Label>Notification Type</Label>
              <Input
                type='select'
                name='notification_type'
                className='custom-select'
                value={notificationData.notification_type}
                onChange={onChangeHandle}
              />
            </FormGroup>

            <FormGroup className='col-sm-12'>
              <Label>Message</Label>
              <Input
                type='textarea'
                rows='4'
                name='message'
                value={notificationData.message}
                onChange={onChangeHandle}
                placeholder='Type message...'
              />
            </FormGroup>

            <FormGroup className='col-sm-6'>
              <Label>SEND TO PROFILE</Label>
              <Input
                type='select'
                name='send_to_profile'
                className='custom-select'
                value={notificationData.send_to_profile}
                onChange={onChangeHandle}
              >
                <option value=''>Select</option>
                <option value='0'>All</option>
                {userProfileList.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.user_profile}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup className='col-sm-3'>
              <Label>Schedule Date</Label>
              <Input
                type='date'
                name='schedule_for'
                value={notificationData.schedule_for || ''}
                onChange={onChangeHandle}
              />
            </FormGroup>

            <FormGroup className='col-sm-3'>
              <Label>Schedule Time</Label>
              <Input
                type='time'
                name='scheduled_time'
                value={notificationData.scheduled_time || ''}
                onChange={onChangeHandle}
              />
            </FormGroup>
          </Row>

          <Button
            color='primary'
            className='float-end mt-3'
            onClick={SaveNotification}
          >
            Save
          </Button>
        </CardBody>
      </Card>
    </>
  );
};
export default AddEditNotification;
